/**
 * Reading text on a team's colour.
 *
 * Team colours are arbitrary user content (an organiser picks their club's
 * kit), so nothing may assume a fixed label colour will read on one. A pale
 * green tested at 3.04:1 against white, which is unreadable in the situation
 * this app is actually used in: outdoors, in direct sun, at arm's length.
 *
 * These live in `src/lib` rather than `shared/` because they are a rendering
 * decision, not a rule the Worker has any opinion about.
 */

import type { Theme } from "./ground";

const CHANNEL_CACHE = new Map<string, number>();

function channel(v: number): number {
  const cached = CHANNEL_CACHE.get(String(v));
  if (cached !== undefined) return cached;
  const s = v / 255;
  const out = s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  CHANNEL_CACHE.set(String(v), out);
  return out;
}

function parse(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (m?.[1] === undefined) return null;
  const h = m[1].length === 3 ? [...m[1]].map((c) => c + c).join("") : m[1];
  return [
    Number.parseInt(h.slice(0, 2), 16),
    Number.parseInt(h.slice(2, 4), 16),
    Number.parseInt(h.slice(4, 6), 16),
  ];
}

function luminance(r: number, g: number, b: number): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function ratio(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** Near-black, matching `--foreground`. */
const INK = "#14171d";
const INK_LUM = luminance(0x14, 0x17, 0x1d);

/**
 * The fill a team colour is actually painted with, and its label: the colour
 * DARKENED until white or near-black clears 4.5:1 on it. One loop, shared by
 * `readableOn` and the edge/clash checks, so the two can never disagree about
 * which fill is on the screen.
 */
function skinOf(hex: string): { rgb: [number, number, number]; fg: string } {
  const rgb = parse(hex);
  // Unparseable: a known-good pair beats emitting something the caller cannot
  // reason about.
  if (rgb === null) return { rgb: [31, 111, 235], fg: "#ffffff" };
  let [r, g, b] = rgb;

  const TARGET = 4.5;
  for (let i = 0; i < 24; i += 1) {
    const l = luminance(r, g, b);
    const white = ratio(l, 1);
    const black = ratio(l, INK_LUM);
    if (white >= TARGET || black >= TARGET) {
      return { rgb: [r, g, b], fg: white >= black ? "#ffffff" : INK };
    }
    // A mid-tone that fails both ways: deepen until white clears the bar.
    r = Math.round(r * 0.9);
    g = Math.round(g * 0.9);
    b = Math.round(b * 0.9);
  }
  return { rgb: [r, g, b], fg: "#ffffff" };
}

/**
 * Pick a readable label for a fill, DARKENING the fill when neither white nor
 * near-black clears 4.5:1 on it. The button has to stay identifiably
 * team-coloured, so the fill moves rather than the colour being discarded.
 */
export function readableOn(hex: string): { bg: string; fg: string } {
  const { rgb, fg } = skinOf(hex);
  return { bg: `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`, fg };
}

/**
 * Whether a team's painted fill needs an EDGE to be seen on its surface.
 *
 * `readableOn` only ever compared the fill with its label, never with what the
 * fill sits on, so a white kit painted a white score target onto a white card:
 * the biggest control on the scorekeeper, with no boundary at all. A pale pink
 * or a yellow is barely better. Under 3:1 against the surface (WCAG 1.4.11's
 * bar for a control boundary) the caller draws a solid ink edge; above it the
 * fill IS the edge, and an extra ring on a saturated kit reads as a pressed or
 * sunken button.
 */
export function needsEdge(hex: string, surface = "#ffffff"): boolean {
  const back = parse(surface);
  if (back === null) return false;
  const [r, g, b] = skinOf(hex).rgb;
  return ratio(luminance(r, g, b), luminance(back[0], back[1], back[2])) < 3;
}

/**
 * What a chip actually sits on, per theme: the white card on the light
 * ground, the glass pane on the dark one (`--card`, white at 5.5% over the
 * `#0b0c10` ground, which paints as #181a1d). The card rather than the page
 * ground: chips live in rows and cards, and on light the card is the
 * stricter of the two.
 */
export const CHIP_SURFACE: Record<Theme, string> = { light: "#ffffff", dark: "#181a1d" };

/**
 * `needsEdge` against the pane the CURRENT theme paints. A white kit is
 * invisible on a white card and fine on dark glass; a black kit the reverse;
 * so the call is made per theme, at render, and the chip draws its ring
 * (`.slant-chip[data-edge="true"]` in theme.css) only where it is needed.
 */
export function kitEdge(hex: string, theme: Theme): boolean {
  return needsEdge(hex, CHIP_SURFACE[theme]);
}

/**
 * Whether two teams' fills are too alike to tell apart at a glance.
 *
 * Real data from the next event: Galas and Galas Pink are both #e191be, Gigolo
 * and Gigolo Black both #000000. A club's colour in this app is its club
 * colour, not necessarily today's shirts, so two identical score targets side
 * by side are an ordinary Saturday. Judged on the PAINTED fills by the
 * "redmean" colour distance, a cheap perceptual weighting of RGB: luminance
 * contrast alone called red against blue a clash (both mid-dark) and missed
 * Odesa's near-black green against Gigolo's black (1.8:1, and the same dark
 * slab in sun). Under 150 the two targets are the same colour to a glance. The
 * scorekeeper answers with names on the targets, and setup with a warning.
 */
export function kitClash(a: string, b: string): boolean {
  const [ar, ag, ab] = skinOf(a).rgb;
  const [br, bg, bb] = skinOf(b).rgb;
  const mean = (ar + br) / 2;
  const dr = ar - br;
  const dg = ag - bg;
  const db = ab - bb;
  const distance = Math.sqrt(
    (2 + mean / 256) * dr * dr + 4 * dg * dg + (2 + (255 - mean) / 256) * db * db,
  );
  return distance < 150;
}

/**
 * White or near-black on a colour, WITHOUT moving the colour. For a small chip
 * where the fill is the identity and the two or three letters on it are a
 * label rather than something anybody reads carefully.
 */
export function onColor(hex: string): string {
  const rgb = parse(hex);
  if (rgb === null) return "#ffffff";
  const [r, g, b] = rgb;
  const l = luminance(r, g, b);
  return ratio(l, 1) >= ratio(l, INK_LUM) ? "#ffffff" : INK;
}
